import { SPACE_ELEMENTS_OF_MODAL } from "@components/common/dialog/faro-dialog";
import { useErrorContext } from "@context-providers/error-boundary/error-handling-context";
import { BaseSnapshotProps } from "@custom-types/base-types";
import { Accordion, Alert } from "@flat-ui";
import { Skeleton } from "@mui/material";
import { SnapshotMemberList } from "@pages/project-details/project-snapshots/snapshot-member-list";
import {
  fetchingProjectsFlagsSelector,
  selectedSnapshotMembersSelector,
} from "@store/projects/projects-selector";
import { fetchSnapshotMembers } from "@store/projects/projects-slice-thunk";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { useEffect } from "react";
import { sortSnapshotMembers } from "@pages/project-details/project-snapshots/snapshot-utils";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { SnapshotEvents } from "@utils/track-event/track-event-list";
import { isUserSnapshotViewOnly } from "@utils/permission-control/snapshot-permission-control";

export interface SnapshotMemberLocationProps {
  /** The location where the snapshot members are being displayed, used for event tracking */
  location: "snapshot-sidepanel" | "snapshot-share-dialog";
}

type Props = BaseSnapshotProps & SnapshotMemberLocationProps;

/** Renders the members of a snapshot in share dialog */
export function SnapshotMembers({ snapshot, location }: Props): JSX.Element {
  const dispatch = useAppDispatch();
  const { trackEvent } = useTrackEvent();
  const { handleErrorWithToast } = useErrorContext();
  const members = useAppSelector(selectedSnapshotMembersSelector);
  const { isFetchingSnapshotMembers } = useAppSelector(
    fetchingProjectsFlagsSelector
  );

  // Filter out resource owners, as they are creator of the snapshot
  const membersWithoutOwner = members.filter(
    (member) => member.role !== "RESOURCE_OWNER"
  );

  const sortedMembers = sortSnapshotMembers(membersWithoutOwner);

  // Fetch the members of the snapshot when the component mounts
  useEffect(() => {
    async function fetchMembers(): Promise<void> {
      try {
        await dispatch(
          fetchSnapshotMembers({ snapshotId: snapshot.id })
        ).unwrap();
      } catch (error) {
        handleErrorWithToast({
          id: `ProjectSnapshots-fetchMembers-${Date.now().toString()}`,
          title: "Could not fetch snapshot members",
          error,
        });
      }
    }

    void fetchMembers();
  }, [dispatch, handleErrorWithToast, snapshot]);

  if (isFetchingSnapshotMembers === "pending") {
    return (
      <Skeleton
        variant="rectangular"
        height="48px"
        sx={{ marginTop: SPACE_ELEMENTS_OF_MODAL }}
      />
    );
  }

  if (isFetchingSnapshotMembers === "rejected") {
    return (
      <Alert
        variant="warning"
        title="Snapshot members could not be fetched"
        sx={{ marginTop: SPACE_ELEMENTS_OF_MODAL }}
      />
    );
  }

  return (
    <Accordion
      isDefaultExpanded={
        location === "snapshot-sidepanel" || isUserSnapshotViewOnly(snapshot)
      }
      title={`${sortedMembers.length} people are invited`}
      onChange={(isExpanded) => {
        trackEvent({
          name: SnapshotEvents.toggleSnapshotMembers,
          props: { isExpanded, location },
        });
      }}
      sx={{
        border: "none",
        "& .MuiButtonBase-root": {
          padding: "0px",
          flexDirection: "row-reverse",
          justifyContent: "flex-end",
          gap: 0.5,
        },
        "& .MuiAccordionSummary-expandIconWrapper svg": {
          fontSize: "18px",
        },
      }}
    >
      <SnapshotMemberList
        members={sortedMembers}
        snapshot={snapshot}
        location={location}
      />
    </Accordion>
  );
}
